import { isPlatformBrowser } from '@angular/common';
import type { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { HttpStatusCode } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, throwError } from 'rxjs';
import { LogoutReason } from '@app/domain/enums';
import type { ErrorResponse } from '@app/domain/interfaces/response/error-response.interface';
import { SNACK_BAR_LONG_DURATION } from '@app/domain/properties';
import { AuthService } from '@app/services/auth/auth';

const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

function stripQuery(url: string): string {
  return url.split('?')[0];
}

/**
 * Only a 401 for the token this tab is using right now ends the session. A request sent with an
 * older token (before a re-login, or a stale request finishing after logout) or with no token at
 * all (SKIP_AUTH calls) says nothing about the current session.
 */
function wasSentWithCurrentToken(req: HttpRequest<unknown>, token: null | string): boolean {
  return token !== null && req.headers.get('Authorization') === `Bearer ${token}`;
}

export const unauthorizedInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const platformId = inject(PLATFORM_ID);
  const snackBar = inject(MatSnackBar);

  return next(req).pipe(
    catchError((err: ErrorResponse) => {
      const isSessionRejected = err.status === HttpStatusCode.Unauthorized
        && wasSentWithCurrentToken(req, authService.accessToken());

      if (isSessionRejected && isPlatformBrowser(platformId)) {
        snackBar.open(SESSION_EXPIRED_MESSAGE, undefined, { duration: SNACK_BAR_LONG_DURATION });
        authService.logout(LogoutReason.UNAUTHORIZED, stripQuery(req.url));
      }

      return throwError(() => err);
    }),
  );
};
